/** Same-origin calls to /api (session cookie). Errors carry the API's code and a user-facing message. */
export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string, readonly body: any = null) {
    super(message);
  }
}

export const SESSION_CHANGED = 'vw:session-changed';

export async function api<T>(path: string, init: { method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; json?: unknown } = {}): Promise<T> {
  const res = await fetch(path, {
    method: init.method ?? 'GET',
    credentials: 'same-origin',
    headers: init.json !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: init.json !== undefined ? JSON.stringify(init.json) : undefined,
  });
  const text = await res.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  // Session changed under us (signed out, banned, consent withdrawn elsewhere): App re-checks /api/me.
  if (!res.ok && path !== '/api/me' && (res.status === 401 || data?.error?.code === 'consent_required' || data?.error?.code === 'banned')) {
    window.dispatchEvent(new Event(SESSION_CHANGED));
  }
  if (!res.ok) throw new ApiError(res.status, data?.error?.code ?? 'http_error', data?.error?.message ?? `Something went wrong (HTTP ${res.status})`, data);
  return data as T;
}

export interface PrivacyState { noticeVersion: string; needsConsent: boolean; consentAt: number | null; withdrawnAt: number | null; eraseAt: number | null; analytics: boolean | null; nominee: { name: string; email: string } | null }

export interface Me {
  user: { id: string; email: string; name: string | null; role: 'user' | 'admin' };
  privacy?: PrivacyState;
  plan: { planSlug?: string; planName?: string; limits?: Record<string, unknown> };
  links?: { website?: string; admin?: string; mcp?: string };
}

export interface CreatorCard {
  igPk: string;
  handle: string | null;
  fullName: string | null;
  bio: string | null;
  bioLinks: Array<{ url: string; title: string | null }>;
  externalUrl: string | null;
  category: string | null;
  isVerified: boolean;
  followers: number | null;
  following: number | null;
  mediaCount: number | null;
  avatarUrl: string | null;
  isPublic: boolean;
  baselineValue: number | null;
  baselineN: number;
  scoreBasis: string | null;
  scoredAt: number | null;
  lastScannedAt: number | null;
  reelCount: number;
  peakOutlier: number | null;
  transcriptCount: number;
  featuredCategory: string | null;
  /** Niches the team set (decision 88), active ones only, in the list's order. */
  niches: NicheChip[];
  /** Decision 91: a collab-only profile (its own Reels tab never scanned): tagged "Collab profile", no scores. */
  isStub?: boolean;
  unavailable?: false;
}
export interface NicheChip { slug: string; name: string }
export type SavedCreator = CreatorCard & { savedAt: number; tags: string[] };
export type LibraryItem = SavedCreator | { igPk: string; unavailable: true; savedAt: number };

export type Visibility = 'private' | 'people' | 'signed_in_link' | 'anyone_link' | 'public';

export interface CollectionSummary {
  id: string; name: string; emoji: string | null; visibility: Visibility; itemCount: number; updatedAt: number; coverUrl: string | null; takedown: boolean;
}

export interface ItemReel {
  pk: string; shortcode: string; ownerPk: string; ownerHandle: string | null; ownerAvatarUrl: string | null; postedAt: number | null;
  durationS: number | null; caption: string | null; views: number | null; likes: number | null; likesHidden: boolean; comments: number | null;
  outlierScore: number | null; viralityScore: number | null; hasTranscript: boolean; isPaidPartnership: boolean; thumbUrl: string | null;
  hook: string | null; hookScript: string | null;
}

export interface BookmarkItem { reelPk: string; note: string | null; createdAt: number; reel: ItemReel | null }

export interface CollectionView {
  id: string; name: string; description: string | null; emoji: string | null; visibility: Visibility; featured: boolean; itemCount: number;
  createdAt: number; updatedAt: number; role: 'owner' | 'viewer' | 'anonymous'; ownerName: string | null;
  takedown: { at: number; reason: string | null } | null;
  items: Array<{ reelPk: string; note: string | null; addedAt: number; reel: ItemReel | null }>;
  link?: string | null; linkAvailable?: boolean; invites?: Array<{ email: string; joined: boolean }>;
  limits?: { items: number; shareLevels: Visibility[] }; publishRequest?: string | null;
}
